import { useState, useRef, useEffect, useCallback } from "react";
import { useConfig } from "../../contexts/config-context.js";
import { useFormStatus, FORM_STATUS } from "../../contexts/form-status-provider.js";
import { useUploadSignature } from "./use-upload-signature.js";
import { decodeDataUrl, isDataUrl, convertToPNGFile } from "./utils/data-url.js";
import { EMPTY_VALUE } from "../../constants/empty-values.js";
const useSignatureValue = ({
  value,
  onChange,
  target
}) => {
  const [signatureValue, setSignatureValue] = useState(value);
  const { isRTL: rtl } = useConfig();
  const uploadSignatureFile = useUploadSignature(target);
  const { setFormStatus } = useFormStatus();
  const uploadTimeoutIdRef = useRef();
  useEffect(() => {
    if (value === EMPTY_VALUE) {
      setSignatureValue(EMPTY_VALUE);
    }
  }, [value]);
  const cancelUpload = useCallback(() => {
    if (uploadTimeoutIdRef.current) {
      clearTimeout(uploadTimeoutIdRef.current);
      uploadTimeoutIdRef.current = null;
    }
  }, []);
  const signature = signatureValue?.[0];
  const decodedValue = signature?.url ? decodeDataUrl(signature.url) : void 0;
  const uploadSignature = useCallback(
    async (_signature) => {
      try {
        const dataUrl = _signature?.url ?? "";
        if (isDataUrl(dataUrl)) {
          const signatureFile = await convertToPNGFile(dataUrl, rtl);
          if (signatureFile) {
            const uploadedValue = await uploadSignatureFile(signatureFile);
            onChange([uploadedValue]);
          } else {
            setSignatureValue(void 0);
            onChange(void 0);
          }
        }
        uploadTimeoutIdRef.current = null;
      } finally {
        setFormStatus(target, FORM_STATUS.READY);
      }
    },
    [setFormStatus, target, rtl, uploadSignatureFile, onChange]
  );
  const handleChange = useCallback(
    (newSignature) => {
      const deferUploadSignature = (_signature) => {
        uploadTimeoutIdRef.current = setTimeout(
          () => uploadSignature(_signature),
          1500
        );
      };
      if (newSignature) {
        if (uploadTimeoutIdRef.current) {
          clearTimeout(uploadTimeoutIdRef.current);
          uploadTimeoutIdRef.current = null;
        }
        setSignatureValue([newSignature]);
        deferUploadSignature(newSignature);
      } else {
        onChange?.(null);
        setSignatureValue(void 0);
        setFormStatus(target, FORM_STATUS.READY);
      }
    },
    [uploadSignature, onChange, setFormStatus, target]
  );
  return {
    handleChange,
    signature,
    decodedValue,
    cancelUpload
  };
};
export {
  useSignatureValue
};
//# sourceMappingURL=use-signature-value.js.map
